"use client";

import React, { useState, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormWatch } from "@/hooks/useFormWatch";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import {
  designerApplicationSchema,
  type DesignerApplicationFormData,
} from "@/lib/validations/designer-application";
import ApplicationProgressSidebar from "./ApplicationProgressSidebar";
import { APPLY_BODY, APPLY_MOBILE_HEADING, APPLY_SUBMIT_LABEL } from "./applyCopy";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Link from "next/link";
import PersonalInformationSection from "./sections/PersonalInformationSection";
import RoleExperienceSection from "./sections/RoleExperienceSection";
import WorkPreferencesSection from "./sections/WorkPreferencesSection";
import AvailabilitySection from "./sections/AvailabilitySection";
import PrivacyPolicySection from "./sections/PrivacyPolicySection";
import { useAutoScrollToNextSection } from "@/hooks/useAutoScrollToNextSection";
import { useIsMobile } from "@/hooks/use-mobile";

interface DesignerApplicationFormProps {
  onEmailChange?: (email: string) => void;
  duplicateEmail?: string | null;
  /** Apply-link code when arriving via /apply/<code> — ties the submission to a job */
  applyCode?: string;
  /** Job title from the apply link — switches the page copy to a job application */
  jobTitle?: string;
  /** Published JD version shown on the apply page — recorded on the application */
  jdVersionId?: string;
}

const DesignerApplicationForm: React.FC<DesignerApplicationFormProps> = ({ onEmailChange, duplicateEmail, applyCode, jobTitle, jdVersionId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingData, setPendingData] = useState<DesignerApplicationFormData | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (submittedEmail) successRef.current?.focus();
  }, [submittedEmail]);
  // Keep the first server/client render identical; hostname is client-only.
  const [isDevelopment, setIsDevelopment] = useState(process.env.NODE_ENV === "development");
  React.useEffect(() => {
    setIsDevelopment(
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      !process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
    );
  }, []);
  const isMobile = useIsMobile();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<DesignerApplicationFormData>({
    resolver: zodResolver(designerApplicationSchema),
    mode: 'onChange', // Enable real-time validation as user types
    defaultValues: {
      phoneCountryCode: "+33",
      workSetup: [],
      languages: [],
      acceptedTermsOfService: false,
      acceptedPrivacyPolicy: false,
      acceptedCookiePolicy: false,
    },
  });

  // Use optimized watch hook to watch multiple fields at once
  const watchedFields = useFormWatch<DesignerApplicationFormData>(control, [
    "fullName",
    "workSetup",
    "languages",
    "email",
    "country",
    "city",
    "phoneCountryCode",
    "phoneNumber",
    "openToRoles",
    "portfolioLink",
    "portfolioPassword",
    "portfolioPasswordValue",
    "linkedInProfile",
    "currentRoleLevel",
    "nextRoleLevel",
    "workEnvironment",
    "expectedSalaryRange",
    "readyToBegin",
    "openToFutureContact",
    "acceptedTermsOfService",
    "acceptedPrivacyPolicy",
    "acceptedCookiePolicy",
  ]);

  const emailValue = watchedFields.email;

  // Call onEmailChange when email changes
  React.useEffect(() => {
    onEmailChange?.(emailValue || "");
  }, [emailValue, onEmailChange]);

  const hasExistingApplication = !applyCode && !!duplicateEmail &&
    duplicateEmail === emailValue?.trim().toLowerCase();

  // Define sections with their fields for auto-scroll
  const sections = useMemo(() => [
    {
      // Personal Information
      fields: [
        "fullName",
        "email",
        "phoneNumber",
        "country",
        "city",
        "openToRoles",
      ] as (keyof DesignerApplicationFormData)[],
    },
    {
      // Role & Experience
      fields: [
        "portfolioLink",
        "portfolioPassword",
        "portfolioPasswordValue",
        "linkedInProfile",
        "currentRoleLevel",
        "languages",
      ] as (keyof DesignerApplicationFormData)[],
    },
    {
      // Work Preferences
      fields: [
        "nextRoleLevel",
        "workSetup",
        "workEnvironment",
        "expectedSalaryRange",
      ] as (keyof DesignerApplicationFormData)[],
    },
    {
      // Availability
      fields: [
        "readyToBegin",
        "openToFutureContact",
      ] as (keyof DesignerApplicationFormData)[],
    },
    {
      // Legal Agreements
      fields: [
        "acceptedTermsOfService",
        "acceptedPrivacyPolicy",
        "acceptedCookiePolicy",
      ] as (keyof DesignerApplicationFormData)[],
    },
  ], []);

  // Auto-scroll to next section when current section becomes complete (desktop only)
  useAutoScrollToNextSection({
    sections,
    watchedFields,
    errors,
    enabled: isMobile === false, // Only enable on desktop (isMobile === false, not undefined)
  });

  const handleFormSubmit = (data: DesignerApplicationFormData) => {
    if (hasExistingApplication || submittedEmail) return;
    setPendingData(data);
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingData || hasExistingApplication || submittedEmail || isSubmitting) return;
    
    setShowConfirmDialog(false);
    setIsSubmitting(true);
    
    try {
      console.log('[ApplicationForm] Submitting application...');
      const response = await fetch("/api/application/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...pendingData, turnstileToken, applyCode, jdVersionId }),
      });

      console.log('[ApplicationForm] Response status:', response.status);
      const result = await response.json();
      console.log('[ApplicationForm] Response data:', result);

      if (!response.ok) {
        const detail = result.details ? ` (${result.details})` : '';
        console.error('[ApplicationForm] Server error details:', result.details, 'code:', result.code);
        throw new Error((result.error || "Failed to submit application") + detail);
      }

      // A persistent, static confirmation works with reduced motion and stays
      // available until the applicant chooses where to go next.
      setSubmittedEmail(pendingData.email);
      setIsSubmitting(false);
      setPendingData(null);
    } catch (error) {
      console.error('[ApplicationForm] Submission error:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to submit application. Please try again.";
      toast.error(errorMessage);
      console.error('[ApplicationForm] Error message shown to user:', errorMessage);
      setIsSubmitting(false);
      // Reset Turnstile on error (only if not in development)
      if (!isDevelopment && turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken("");
    }
  };


  if (submittedEmail) {
    return (
      <div
        ref={successRef}
        role="status"
        tabIndex={-1}
        className="my-8 rounded-xl border border-green-200 bg-white p-6 md:p-10 focus:outline-none"
      >
        <h1 className="text-3xl font-prociono text-gray-900">Application submitted</h1>
        <p className="mt-4 text-gray-700">
          Thank you. We&apos;ve received your application and will contact you at{" "}
          <span className="font-medium">{submittedEmail}</span> about the next steps.
        </p>
        <Link
          href="/"
          className="mt-6 inline-flex min-h-11 items-center rounded-lg bg-black px-5 py-2 text-sm font-medium text-white hover:bg-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          Back to homepage
        </Link>
      </div>
    );
  }


  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 mt-6 md:border-2 md:border-gray-200 md:rounded-lg md:bg-white items-stretch min-h-screen">
        {/* Sidebar Section - takes 1 column on desktop, hidden on mobile */}
        <div className="lg:col-span-1 lg:rounded-l-lg lg:bg-[#FAFAF9]">
          <div className="sticky top-[80px] max-h-[calc(100vh-80px)] overflow-hidden">
            <ApplicationProgressSidebar
              watchedFields={watchedFields}
              errors={errors}
              jobTitle={jobTitle}
            />
          </div>
        </div>
        
        {/* Form Section - takes 2 columns on desktop */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit(handleFormSubmit)} className="p-4 md:p-12 flex flex-col gap-8 md:bg-white md:rounded-r-lg">
            <div className="bg-[#FAFAF9] flex flex-col border-2 border-gray-200 shadow-sm rounded-xl px-4 py-6 lg:hidden">
              <div className="flex flex-col gap-3">
                <h1
                  className="text-4xl font-normal leading-tight tracking-tight font-prociono"
                  style={{
                    background: "linear-gradient(90deg, #231617 0%, #89565A 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {jobTitle ? APPLY_MOBILE_HEADING : "Join our designer network"}
                </h1>
                <p className="text-sm text-neutral-500">{jobTitle ? APPLY_BODY : "We review designer profiles for the roles we're recruiting for. Share your experience, preferences, and the work you want next so we can consider you for relevant opportunities."}</p>

                <p className="text-gray-600 text-sm"> Takes 2 minutes to complete </p>
              </div>
            </div>

            <div role="status" aria-live="polite">
              {hasExistingApplication && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900">
                  <h2 className="font-semibold">Application already submitted</h2>
                  <p className="mt-1 text-sm">
                    This email already has an application. Our team will review it and get back to you.
                    If you entered the wrong address, correct it below; your other answers will stay here.
                  </p>
                </div>
              )}
            </div>

            <div data-section-index="0">
              <PersonalInformationSection
                control={control}
                setValue={setValue}
                watchedFields={watchedFields}
                errors={errors}
                register={register}
              />
            </div>

            <div data-section-index="1">
              <RoleExperienceSection
                control={control}
                setValue={setValue}
                watchedFields={watchedFields}
                errors={errors}
                register={register}
              />
            </div>

            <div data-section-index="2">
              <WorkPreferencesSection
                control={control}
                setValue={setValue}
                watchedFields={watchedFields}
                errors={errors}
                register={register}
              />
            </div>

            <div data-section-index="3">
              <AvailabilitySection
                control={control}
                setValue={setValue}
                watchedFields={watchedFields}
                errors={errors}
                register={register}
              />
            </div>

            <div data-section-index="4">
              <PrivacyPolicySection
                control={control}
                setValue={setValue}
                watchedFields={watchedFields}
                errors={errors}
                register={register}
              />
            </div>

            {/* Turnstile and Submit Button */}
            <div className="flex flex-col gap-4">
              {!isDevelopment && (
                <div className="flex justify-start">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ""}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onError={() => setTurnstileToken("")}
                    onExpire={() => setTurnstileToken("")}
                    options={{
                      theme: "light",
                      size: "normal",
                    }}
                  />
                </div>
              )}
              <Button
                type="submit"
                disabled={isSubmitting || hasExistingApplication || (!isDevelopment && !turnstileToken)}
                className="bg-black text-white hover:bg-gray-900 px-4 py-2 h-auto font-medium w-fit"
              >
                {isSubmitting ? "Submitting..." : jobTitle ? APPLY_SUBMIT_LABEL : "Submit Profile"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </form>
        </div>
      </div>

    <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
      <DialogContent className="bg-white p-8 rounded-lg">
        <DialogHeader>
          <DialogTitle>Confirm Submission</DialogTitle>
          <DialogDescription>
            Are you sure you want to submit your application? Please review all information before proceeding.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setShowConfirmDialog(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            disabled={isSubmitting || hasExistingApplication}
            className="bg-black text-white hover:bg-gray-900"
          >
            {isSubmitting ? "Submitting..." : "Confirm & Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default DesignerApplicationForm;
